/**
 * AuditSessionControlBar Component
 *
 * A sticky control bar that appears when the user scrolls past the filter control bar.
 * Provides quick access to audit completion functionality and displays progress statistics.
 */

import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { useGetAuditSessionStats } from '@internal-audit/hooks/useGetAuditSessionStats';
import { Button, ButtonType, ButtonSize, OverlayMode, Text, IconButton, ArrowUp, IconSize } from '@soteria/design-system-ve';
import { useMemo, useCallback } from 'react';

import { ProgressBar } from '../../../core/components';

import * as styles from './AuditSessionControlBar.css';

export interface AuditSessionControlBarProps {
  /** The audit session data */
  session: FlexibleAuditSession | null;
  /** Number of filtered items currently displayed */
  filteredItemsCount: number;
  /** Whether the control bar should be visible */
  isVisible: boolean;
  /** Handler for completing the audit */
  onCompleteAudit: () => void;
  /** Loading state */
  isLoading?: boolean;
}

export function AuditSessionControlBar({ session, filteredItemsCount, isVisible, onCompleteAudit, isLoading = false }: AuditSessionControlBarProps) {
  // Use the audit stats hook for accurate server-side calculations
  const { stats, utils } = useGetAuditSessionStats(session?.id, {
    skip: !session?.id,
    pollInterval: 30000, // Poll every 30 seconds for live updates
  });

  // Fallback to session data if stats are not available yet
  const progressStats = useMemo(() => {
    if (stats && utils) {
      return {
        totalItems: stats.totalCount,
        completedItems: stats.completeCount,
        skippedItems: stats.skippedCount,
        remainingItems: stats.incompleteCount,
        completionPercentage: utils.getCompletionPercentage(),
      };
    }

    // Fallback to session data for immediate display
    if (!session) {
      return {
        totalItems: 0,
        completedItems: 0,
        skippedItems: 0,
        remainingItems: 0,
        completionPercentage: 0,
      };
    }

    const totalItems = session.itemsCount || 0;
    const completedItems = session.completedCount || 0;
    const skippedItems = session.skippedCount || 0;
    const remainingItems = totalItems - completedItems - skippedItems;
    const completionPercentage = totalItems === 0 ? 0 : Math.round(((completedItems + skippedItems) / totalItems) * 100);

    return {
      totalItems,
      completedItems,
      skippedItems,
      remainingItems,
      completionPercentage,
    };
  }, [stats, utils, session]);

  // Handle scroll to top
  const handleScrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  // Don't render if no session data
  if (!session) {
    return null;
  }

  return (
    <div aria-label="Audit session control bar" className={styles.controlBarContainer} data-visible={isVisible} role="banner">
      <div className={styles.controlBarContent}>
        {/* Left Section - Items Count (styled like filter bar) */}
        <div className={styles.leftSection}>
          <span className={styles.filterResults}>{filteredItemsCount} items</span>
        </div>

        {/* Center Section - Progress Statistics */}
        <div className={styles.centerSection}>
          <div className={styles.progressStats}>
            <div className={styles.statItem}>
              <Text className={styles.statLabel}>Total</Text>
              <Text className={styles.statValue}>{progressStats.totalItems}</Text>
            </div>

            <div className={styles.statSeparator} />

            <div className={styles.statItem}>
              <Text className={styles.statLabel}>Completed</Text>
              <Text className={styles.statValue}>{progressStats.completedItems}</Text>
            </div>

            <div className={styles.statSeparator} />

            <div className={styles.statItem}>
              <Text className={styles.statLabel}>Skipped</Text>
              <Text className={styles.statValue}>{progressStats.skippedItems}</Text>
            </div>

            <div className={styles.statSeparator} />

            <div className={styles.statItem}>
              <Text className={styles.statLabel}>Remaining</Text>
              <Text className={styles.statValue}>{progressStats.remainingItems}</Text>
            </div>
          </div>
        </div>

        {/* Right Section - Completion Percentage and Complete Audit Button */}
        <div className={styles.rightSection}>
          <ProgressBar
            animated
            aria-label={`Audit completion progress: ${progressStats.completionPercentage}% complete`}
            color="primary"
            size="medium"
            text={`${progressStats.completionPercentage}% Complete`}
            textAlign="right"
            textPosition="before"
            value={progressStats.completionPercentage}
          />

          <Button
            buttonSize={ButtonSize.Default}
            buttonType={ButtonType.Brand}
            disabled={isLoading || session.status === 'completed'}
            overlayMode={OverlayMode.Solid}
            onClick={onCompleteAudit}
          >
            COMPLETE AUDIT
          </Button>

          <IconButton
            aria-label="Scroll to top"
            buttonType={ButtonType.Secondary}
            icon={ArrowUp}
            iconSize={IconSize.Small}
            overlayMode={OverlayMode.Solid}
            title="Scroll to top"
            onClick={handleScrollToTop}
          />
        </div>
      </div>
    </div>
  );
}
